import { z } from "zod";
import type { HttpClient } from "./http";
import { platformFeaturesSchema, workspaceModulesSchema, type ModuleGrantUpdate } from "./modules";
import {
  auditEventSchema,
  customerIncidentsSchema,
  organizationDetailSchema,
  pageOf,
  domainSchema,
  planSchema,
  productSchema,
  provisionedRefSchema,
  provisioningRequestSchema,
  searchHitSchema,
  supportNoteSchema,
  workspaceHealthSchema,
  workspaceIncidentsSchema,
  workspaceSchema,
  type DomainStatus,
  type ProvisioningStatus,
  type WorkspaceStatus,
} from "./schemas";

/** Every endpoint the ops console uses. */
export function opsApi(http: HttpClient) {
  return {
    features: () => http.request("/api/v1/identity/features", { schema: platformFeaturesSchema }),

    workspaceModules: (workspaceId: string) =>
      http.request("/api/v1/ops/workspaces/" + encodeURIComponent(workspaceId) + "/modules", {
        credentials: "include",
        schema: workspaceModulesSchema,
      }),

    setWorkspaceModules: (workspaceId: string, body: ModuleGrantUpdate) =>
      http.request("/api/v1/ops/workspaces/" + encodeURIComponent(workspaceId) + "/modules", {
        method: "PUT", credentials: "include", body, schema: workspaceModulesSchema,
      }),

    retryWorkspaceModules: (workspaceId: string, expectedRevision: number) =>
      http.request("/api/v1/ops/workspaces/" + encodeURIComponent(workspaceId) + "/modules/retry", {
        method: "POST", credentials: "include", body: { expectedRevision }, schema: workspaceModulesSchema,
      }),

    search: (q: string) =>
      http.request("/api/v1/ops/search", { query: { q }, schema: z.array(searchHitSchema) }),

    organization: (organizationId: string) =>
      http.request(`/api/v1/ops/organizations/${organizationId}`, {
        schema: organizationDetailSchema,
      }),

    health: (organizationId: string) =>
      http.request(`/api/v1/ops/organizations/${organizationId}/health`, {
        schema: z.array(workspaceHealthSchema),
      }),

    incidents: (organizationId: string) =>
      http.request(`/api/v1/ops/organizations/${organizationId}/incidents`, {
        schema: z.array(workspaceIncidentsSchema),
      }),

    incidentInbox: () =>
      http.request("/api/v1/ops/incidents", { schema: z.array(customerIncidentsSchema) }),

    notes: (organizationId: string) =>
      http.request(`/api/v1/ops/organizations/${organizationId}/notes`, {
        schema: z.array(supportNoteSchema),
      }),

    addNote: (organizationId: string, body: { body: string; pinned: boolean }) =>
      http.request(`/api/v1/ops/organizations/${organizationId}/notes`, {
        method: "POST",
        body,
        schema: supportNoteSchema,
      }),

    requests: (status?: ProvisioningStatus, page = 0, size = 25) =>
      http.request("/api/v1/ops/requests", {
        query: { status, page, size },
        schema: pageOf(provisioningRequestSchema),
      }),

    queueSummary: () =>
      http.request("/api/v1/ops/requests/summary", { schema: z.object({ pending: z.number() }) }),

    decide: (requestId: string, decision: "approve" | "reject" | "complete", note?: string) =>
      http.request(`/api/v1/ops/requests/${requestId}/${decision}`, {
        method: "POST",
        body: { note },
        schema: provisioningRequestSchema,
      }),

    createWorkspace: (body: {
      organizationId: string;
      productCode: string;
      code: string;
      name: string;
      planCode?: string;
      entitlements?: Record<string, string>;
    }) => http.request("/api/v1/ops/workspaces", { method: "POST", body, schema: workspaceSchema }),

    changeWorkspaceStatus: (workspaceId: string, status: WorkspaceStatus) =>
      http.request(`/api/v1/ops/workspaces/${workspaceId}/status`, {
        method: "POST",
        body: { status },
        schema: workspaceSchema,
      }),

    setEntitlements: (workspaceId: string, entitlements: Record<string, string>) =>
      http.request(`/api/v1/ops/workspaces/${workspaceId}/entitlements`, {
        method: "PATCH",
        body: { entitlements },
        schema: workspaceSchema,
      }),

    createCompany: (
      workspaceId: string,
      body: { code: string; name: string; nameBn?: string; phone1?: string; isGroup?: boolean },
    ) =>
      http.request(`/api/v1/ops/workspaces/${workspaceId}/companies`, {
        method: "POST",
        body,
        schema: provisionedRefSchema,
      }),

    createMarketplace: (
      workspaceId: string,
      body: { code: string; name: string; nameBn?: string; domain?: string; appCode?: string },
    ) =>
      http.request(`/api/v1/ops/workspaces/${workspaceId}/marketplaces`, {
        method: "POST",
        body,
        schema: provisionedRefSchema,
      }),

    domains: () => http.request("/api/v1/ops/domains", { schema: z.array(domainSchema) }),

    changeDomainStatus: (domainId: string, status: DomainStatus) =>
      http.request(`/api/v1/ops/domains/${domainId}/status`, {
        method: "POST",
        body: { status },
        schema: domainSchema,
      }),

    products: () =>
      http.request("/api/v1/ops/catalog/products", { schema: z.array(productSchema) }),

    plans: (productCode?: string) =>
      http.request("/api/v1/ops/catalog/plans", {
        query: { productCode },
        schema: z.array(planSchema),
      }),

    audit: (page = 0, size = 50) =>
      http.request("/api/v1/ops/audit", { query: { page, size }, schema: pageOf(auditEventSchema) }),
  };
}

export type OpsApi = ReturnType<typeof opsApi>;
